import React, { useState, useEffect } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import "./RegistrationForm.scss";
import {
  IAccountType,
  ICreateUserData,
} from "../../interfaces/userManagement.interface";
import { showToast } from "../Toaster";
import UserManagementService from "../../services/UserManagementService";
import { useAuth } from "../../contexts/AuthContext";

interface RegistrationFormProps {
  accountType: IAccountType;
  onBack: () => void;
  onSuccess: (userData: any) => void;
  isSelfRegistration?: boolean;
}

const RegistrationForm: React.FC<RegistrationFormProps> = ({
  accountType,
  onBack,
  onSuccess,
  isSelfRegistration = false,
}) => {
  const { userProfile } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Generate username from full name and organization name (12 chars max)
  const generateUsername = (fullName: string, orgName: string): string => {
    const namePart = fullName.replace(/\s+/g, "").toLowerCase();
    const orgPart = orgName.replace(/\s+/g, "").toLowerCase();

    // Combine and limit to 12 characters
    const combined = (namePart + orgPart).replace(/[^a-z0-9]/g, "");
    return combined.substring(0, 12);
  };

  const validationSchema = Yup.object({
    name: Yup.string()
      .min(2, "Name must be at least 2 characters")
      .max(50, "Name must be less than 50 characters")
      .required("Name is required"),
    organizationName: Yup.string()
      .min(2, "Organization name must be at least 2 characters")
      .max(50, "Organization name must be less than 50 characters")
      .required("Organization name is required"),
    email: Yup.string()
      .email("Invalid email address")
      .required("Email is required"),
    phoneNumber: Yup.string()
      .matches(/^[0-9]{10}$/, "Phone number must be exactly 10 digits")
      .required("Phone number is required"),
    password: Yup.string()
      .min(8, "Password must be at least 8 characters")
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
        "Password must contain at least one lowercase letter, one uppercase letter, and one number"
      )
      .required("Password is required"),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password")], "Passwords must match")
      .required("Confirm password is required"),
  });

  const initialValues: ICreateUserData = {
    name: "",
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    phoneNumber: "",
    identifier: "",
    organizationName: "",
    userType: accountType.type,
  };

  const handleSubmit = async (values: ICreateUserData) => {
    setIsSubmitting(true);
    try {
      // Generate username and identifier
      const username = generateUsername(
        values.name,
        values.organizationName || ""
      );
      const identifier =
        values.organizationName?.toUpperCase().replace(/\s+/g, "_") || "";

      const formData = {
        ...values,
        username,
        identifier,
      };

      let result;

      if (isSelfRegistration) {
        result = await UserManagementService.selfRegister(formData);
      } else {
        result = await UserManagementService.createUser(
          formData,
          userProfile || undefined
        );
      }

      showToast("Registration successful!", "success");
      onSuccess(result);
    } catch (error: any) {
      console.error("Registration error:", error);
      const errorMessage =
        error.response?.data?.message ||
        "Registration failed. Please try again.";
      showToast(errorMessage, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="registration-form">
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({
          isSubmitting: formikSubmitting,
          errors,
          touched,
          values,
          setFieldValue,
        }) => (
          <Form>
            <div className="form-field-cover">
              <div className="form-group">
                <div className="form-field">
                  <label htmlFor="name" className="required_field_label">
                    Full Name
                  </label>
                  <Field
                    type="text"
                    name="name"
                    placeholder="Enter your full name"
                    className={
                      touched.name && errors.name ? "invalid_field" : ""
                    }
                  />
                </div>
                <ErrorMessage
                  name="name"
                  component="div"
                  className="validation_error"
                />
              </div>

              <div className="form-group">
                <div className="form-field">
                  <label
                    htmlFor="organizationName"
                    className="required_field_label"
                  >
                    Organization Name
                  </label>
                  <Field
                    type="text"
                    name="organizationName"
                    placeholder="Enter organization name"
                    className={
                      touched.organizationName && errors.organizationName
                        ? "invalid_field"
                        : ""
                    }
                  />
                </div>
                <ErrorMessage
                  name="organizationName"
                  component="div"
                  className="validation_error"
                />
              </div>

              <div className="form-group">
                <div className="form-field">
                  <label htmlFor="email" className="required_field_label">
                    Email Address
                  </label>
                  <Field
                    type="email"
                    name="email"
                    placeholder="Enter your email"
                    className={
                      touched.email && errors.email ? "invalid_field" : ""
                    }
                  />
                </div>
                <ErrorMessage
                  name="email"
                  component="div"
                  className="validation_error"
                />
              </div>

              <div className="form-group">
                <div className="form-field">
                  <label htmlFor="phoneNumber" className="required_field_label">
                    Phone Number
                  </label>
                  <Field
                    type="tel"
                    name="phoneNumber"
                    placeholder="Enter 10-digit phone number"
                    className={
                      touched.phoneNumber && errors.phoneNumber
                        ? "invalid_field"
                        : ""
                    }
                  />
                </div>
                <ErrorMessage
                  name="phoneNumber"
                  component="div"
                  className="validation_error"
                />
              </div>

              <div className="form-group">
                <div className="form-field">
                  <label htmlFor="password" className="required_field_label">
                    Password
                  </label>
                  <Field
                    type="password"
                    name="password"
                    placeholder="Enter password"
                    className={
                      touched.password && errors.password ? "invalid_field" : ""
                    }
                  />
                </div>
                <ErrorMessage
                  name="password"
                  component="div"
                  className="validation_error"
                />
              </div>

              <div className="form-group">
                <div className="form-field">
                  <label
                    htmlFor="confirmPassword"
                    className="required_field_label"
                  >
                    Confirm Password
                  </label>
                  <Field
                    type="password"
                    name="confirmPassword"
                    placeholder="Confirm your password"
                    className={
                      touched.confirmPassword && errors.confirmPassword
                        ? "invalid_field"
                        : ""
                    }
                  />
                </div>
                <ErrorMessage
                  name="confirmPassword"
                  component="div"
                  className="validation_error"
                />
              </div>
            </div>

            {/* Auto-generated fields info */}
            {/* <div className="auto-generated-info">
              <div className="info-item">
                <strong>Username:</strong>{" "}
                {generateUsername(
                  values.name || "",
                  values.organizationName || ""
                )}
                <small>(Auto-generated from name and organization)</small>
              </div>
              <div className="info-item">
                <strong>Organization Identifier:</strong>{" "}
                {(values.organizationName || "")
                  .toUpperCase()
                  .replace(/\s+/g, "_")}
                <small>(Auto-generated from organization name)</small>
              </div>
            </div> */}

            <div className="form-actions">
              <button
                type="button"
                className="secondary_button"
                onClick={onBack}
                disabled={isSubmitting}
              >
                Back
              </button>
              <button
                type="submit"
                className="primary_button"
                disabled={isSubmitting || formikSubmitting}
              >
                {isSubmitting ? (
                  <div className="loading-spinner">
                    <div className="spinner"></div>
                    Creating Account...
                  </div>
                ) : (
                  `Create ${accountType.title} Account`
                )}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default RegistrationForm;
